import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MailX, Search } from 'lucide-react';
import {
  listUnsubscribedContacts, listBouncedContacts, listCampaignBounces,
  type Contact, type CampaignBounceRow,
} from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { formatNumber } from '../../lib/format';

const fmtZeit = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const tageSeit = (iso: string | null | undefined): number =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : 9999;

const name = (c: Contact) =>
  [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';

/**
 * Abmeldungen & Bounces an einer Stelle.
 *
 * Abmeldungen: Kontakte, die den Abmelde-Link geklickt haben — der stündliche
 * Blacklist-Abgleich holt sie aus Brevo zurück. Bounces: je Kampagne aus der
 * Brevo-Statistik (Hard = Adresse existiert nicht, Soft = Postfach voll o. ä.);
 * am einzelnen Kontakt steht ein Bounce erst, wenn Brevo ihn dauerhaft sperrt.
 */
export function AbmeldungenBounces() {
  const [query, setQuery] = useState('');

  const { data: abmeldungen = [], isLoading: ladeAb, error: fehlerAb } =
    useQuery({ queryKey: ['unsubscribed-contacts'], queryFn: listUnsubscribedContacts });
  const { data: bounceKontakte = [], isLoading: ladeBk, error: fehlerBk } =
    useQuery({ queryKey: ['bounced-contacts'], queryFn: listBouncedContacts });
  const { data: kampagnen = [], isLoading: ladeKamp, error: fehlerKamp } =
    useQuery({ queryKey: ['campaign-bounces'], queryFn: () => listCampaignBounces(30) });

  const kpis = useMemo(() => ({
    gesamt: abmeldungen.length,
    tage7:  abmeldungen.filter(c => tageSeit(c.unsubscribed_at) < 7).length,
    tage30: abmeldungen.filter(c => tageSeit(c.unsubscribed_at) < 30).length,
    hard7:  kampagnen.filter(k => tageSeit(k.sent_at) < 7).reduce((s, k) => s + k.hard_bounces, 0),
    soft7:  kampagnen.filter(k => tageSeit(k.sent_at) < 7).reduce((s, k) => s + k.soft_bounces, 0),
  }), [abmeldungen, kampagnen]);

  const gefiltert = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return abmeldungen;
    return abmeldungen.filter(c =>
      `${name(c)} ${c.email} ${c.organization ?? ''}`.toLowerCase().includes(q));
  }, [abmeldungen, query]);

  return (
    <div>
      <PageHeader
        title="Abmeldungen & Bounces"
        subtitle="Wer will keine Newsletter mehr — und wo kommen Mails nicht an. Abmeldungen kommen stündlich aus Brevo zurück; Bounce-Zahlen je Kampagne aus der Brevo-Statistik."
      />

      <div className="kpi-grid">
        <div className="kpi-card">
          <p className="kpi-label">Abmeldungen gesamt</p>
          <p className="kpi-value">{formatNumber(kpis.gesamt)}</p>
          <p className="kpi-hint">seit Beginn der Zählung</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Letzte 7 Tage</p>
          <p className="kpi-value">{formatNumber(kpis.tage7)}</p>
          <p className="kpi-hint">letzte 30 Tage: {formatNumber(kpis.tage30)}</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Hard Bounces (Kampagnen, 7 Tage)</p>
          <p className="kpi-value">{formatNumber(kpis.hard7)}</p>
          <p className="kpi-hint">Adresse existiert nicht — Kontakt prüfen</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Soft Bounces (Kampagnen, 7 Tage)</p>
          <p className="kpi-value">{formatNumber(kpis.soft7)}</p>
          <p className="kpi-hint">Postfach voll o. ä. — meist vorübergehend</p>
        </div>
      </div>

      <h3 style={{ margin: '0 0 0.75rem' }}>Abmeldungen</h3>
      <div className="search-input-wrapper" style={{ marginBottom: '0.75rem' }}>
        <Search size={16} />
        <input
          className="search-input"
          placeholder="Name, E-Mail oder Standort…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {ladeAb ? (
        <p style={{ color: 'var(--text-secondary)' }}>Lädt…</p>
      ) : fehlerAb ? (
        <EmptyState icon={MailX} title="Abmeldungen nicht ladbar"
          description={fehlerAb instanceof Error ? fehlerAb.message : String(fehlerAb)} />
      ) : gefiltert.length === 0 ? (
        <EmptyState icon={MailX} title="Keine Abmeldungen"
          description={query ? 'Zu dieser Suche gibt es keine Treffer.' : 'Bisher hat sich niemand abgemeldet.'} />
      ) : (
        <table className="data-table" style={{ marginBottom: '2rem' }}>
          <thead>
            <tr>
              <th>Person</th>
              <th>E-Mail</th>
              <th>Standort</th>
              <th>Abgemeldet am</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.slice(0, 200).map(c => (
              <tr key={c.id}>
                <td>{name(c)}</td>
                <td style={{ fontSize: '0.875rem' }}>{c.email}</td>
                <td>{c.organization ?? c.school ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {fmtZeit(c.unsubscribed_at)}
                  {tageSeit(c.unsubscribed_at) < 7 && <span className="status-badge" style={{ marginLeft: '0.4rem', background: 'var(--warning-light, rgba(217,119,6,0.12))', color: 'var(--warning, #b45309)' }}>neu</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {gefiltert.length > 200 && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginTop: '-1.5rem', marginBottom: '2rem' }}>
          Zeigt die 200 neuesten von {formatNumber(gefiltert.length)} Abmeldungen.
        </p>
      )}

      <h3 style={{ margin: '0 0 0.75rem' }}>Bounces je Kampagne</h3>
      {ladeKamp ? (
        <p style={{ color: 'var(--text-secondary)' }}>Lädt…</p>
      ) : fehlerKamp ? (
        <EmptyState icon={MailX} title="Kampagnen-Bounces nicht ladbar"
          description={fehlerKamp instanceof Error ? fehlerKamp.message : String(fehlerKamp)} />
      ) : kampagnen.length === 0 ? (
        <EmptyState icon={MailX} title="Noch keine versendeten Kampagnen"
          description="Sobald eine Kampagne versendet ist, stehen hier ihre Bounce-Zahlen." />
      ) : (
        <table className="data-table" style={{ marginBottom: '2rem' }}>
          <thead>
            <tr>
              <th>Kampagne</th>
              <th>Versendet</th>
              <th style={{ textAlign: 'right' }}>Empfänger</th>
              <th style={{ textAlign: 'right' }}>Hard</th>
              <th style={{ textAlign: 'right' }}>Soft</th>
              <th style={{ textAlign: 'right' }}>Abmeldungen</th>
              <th style={{ textAlign: 'right' }}>Beschwerden</th>
            </tr>
          </thead>
          <tbody>
            {kampagnen.map((k: CampaignBounceRow) => (
              <tr key={k.id}>
                <td><Link to={`/campaigns/${k.id}/stats`}>{k.name}</Link></td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtZeit(k.sent_at)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(k.sent_count)}</td>
                <td style={{ textAlign: 'right', color: k.hard_bounces > 0 ? 'var(--danger, #b91c1c)' : undefined, fontWeight: k.hard_bounces > 0 ? 600 : 400 }}>{formatNumber(k.hard_bounces)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(k.soft_bounces)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(k.unsubscribed)}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(k.complaints)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={{ margin: '0 0 0.75rem' }}>Kontakte mit dauerhaftem Zustellproblem</h3>
      {ladeBk ? (
        <p style={{ color: 'var(--text-secondary)' }}>Lädt…</p>
      ) : fehlerBk ? (
        <EmptyState icon={MailX} title="Kontakt-Bounces nicht ladbar"
          description={fehlerBk instanceof Error ? fehlerBk.message : String(fehlerBk)} />
      ) : bounceKontakte.length === 0 ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
          Aktuell ist an keinem Kontakt ein dauerhafter Bounce vermerkt. Brevo setzt den Vermerk
          erst, wenn eine Adresse endgültig gesperrt wird — einzelne Bounces stehen oben bei den Kampagnen.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>Person</th><th>E-Mail</th><th>Standort</th><th>Status</th></tr>
          </thead>
          <tbody>
            {bounceKontakte.slice(0, 200).map(c => (
              <tr key={c.id}>
                <td>{name(c)}</td>
                <td style={{ fontSize: '0.875rem' }}>{c.email}</td>
                <td>{c.organization ?? c.school ?? '—'}</td>
                <td>{c.bounce_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
